import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { FaTask, FaTaskPriority, FaTaskRecurrence, FaTaskStatus } from '../fa-tasks.model';
import { Opportunity } from '../client-actions.model';

const STATUS_OPTIONS: { label: string; value: FaTaskStatus }[] = [
  { label: 'To Do', value: 'To Do' },
  { label: 'In Progress', value: 'In Progress' },
  { label: 'Done', value: 'Done' },
  { label: 'Blocked', value: 'Blocked' },
  { label: 'Withdrawn', value: 'Withdrawn' },
];

const PRIORITY_OPTIONS: { label: string; value: FaTaskPriority }[] = [
  { label: 'P1 — Critical', value: 'P1' },
  { label: 'P2 — Important', value: 'P2' },
  { label: 'P3 — Normal', value: 'P3' },
  { label: 'P4 — No Priority', value: 'P4' },
];

const RECURRENCE_OPTIONS: { label: string; value: FaTaskRecurrence }[] = [
  { label: 'None', value: 'none' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Custom', value: 'custom' },
];

@Component({
  selector: 'bai-fa-task-add-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, Button, InputTextModule, SelectModule, TextareaModule],
  template: `
    <div class="flex flex-col gap-4 p-2">

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="fat-task-name">
          Task Name <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="fat-task-name"
          pInputText
          [(ngModel)]="form().taskName"
          (ngModelChange)="patchForm('taskName', $event)"
          placeholder="Short task title"
          class="w-full"
          aria-required="true"
        />
        @if (submitted() && !form().taskName) {
          <span class="text-xs text-red-500" role="alert">Required</span>
        }
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="fat-description">
          Description
        </label>
        <textarea
          id="fat-description"
          pTextarea
          [(ngModel)]="form().description"
          (ngModelChange)="patchForm('description', $event)"
          placeholder="Optional details"
          rows="2"
          class="w-full"
        ></textarea>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="fat-assignee">
          Assignee <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="fat-assignee"
          pInputText
          [(ngModel)]="form().assignee"
          (ngModelChange)="patchForm('assignee', $event)"
          placeholder="e.g. Sarah Chen"
          class="w-full"
          aria-required="true"
        />
        @if (submitted() && !form().assignee) {
          <span class="text-xs text-red-500" role="alert">Required</span>
        }
      </div>

      @if (opportunityOptions.length > 0) {
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="fat-opportunity">
            Linked Opportunity
          </label>
          <p-select
            inputId="fat-opportunity"
            [options]="opportunityOptions"
            [(ngModel)]="form().opportunityId"
            (ngModelChange)="patchForm('opportunityId', $event)"
            optionLabel="label"
            optionValue="value"
            placeholder="Select opportunity (optional)"
            styleClass="w-full"
            [showClear]="true"
          />
        </div>
      }

      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="fat-priority">
            Priority <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <p-select
            inputId="fat-priority"
            [options]="priorityOptions"
            [(ngModel)]="form().priority"
            (ngModelChange)="patchForm('priority', $event)"
            optionLabel="label"
            optionValue="value"
            placeholder="Select priority"
            styleClass="w-full"
            aria-required="true"
          />
          @if (submitted() && !form().priority) {
            <span class="text-xs text-red-500" role="alert">Required</span>
          }
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="fat-status">
            Status <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <p-select
            inputId="fat-status"
            [options]="statusOptions"
            [(ngModel)]="form().status"
            (ngModelChange)="patchForm('status', $event)"
            optionLabel="label"
            optionValue="value"
            placeholder="Select status"
            styleClass="w-full"
            aria-required="true"
          />
          @if (submitted() && !form().status) {
            <span class="text-xs text-red-500" role="alert">Required</span>
          }
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="fat-due-date">
            Due Date <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="fat-due-date"
            pInputText
            type="date"
            [(ngModel)]="form().dueDate"
            (ngModelChange)="patchForm('dueDate', $event)"
            class="w-full"
            aria-required="true"
          />
          @if (submitted() && !form().dueDate) {
            <span class="text-xs text-red-500" role="alert">Required</span>
          }
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="fat-planned-date">
            Planned Date
          </label>
          <input
            id="fat-planned-date"
            pInputText
            type="date"
            [(ngModel)]="form().plannedDate"
            (ngModelChange)="patchForm('plannedDate', $event)"
            class="w-full"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="fat-labels">
          Labels <span class="text-xs text-gray-400">(comma-separated)</span>
        </label>
        <input
          id="fat-labels"
          pInputText
          [(ngModel)]="labelsInput"
          placeholder="e.g. IRA, Review, Urgent"
          class="w-full"
          aria-label="Labels, comma-separated"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="fat-recurrence">
          Recurrence
        </label>
        <p-select
          inputId="fat-recurrence"
          [options]="recurrenceOptions"
          [(ngModel)]="form().recurrence"
          (ngModelChange)="patchForm('recurrence', $event)"
          optionLabel="label"
          optionValue="value"
          styleClass="w-full"
        />
      </div>

      @if (form().recurrence === 'custom') {
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="fat-recurrence-text">
            Custom Recurrence Description
          </label>
          <input
            id="fat-recurrence-text"
            pInputText
            [(ngModel)]="form().recurrenceCustomText"
            (ngModelChange)="patchForm('recurrenceCustomText', $event)"
            placeholder="e.g. Every quarter end"
            class="w-full"
          />
        </div>
      }

      <div class="flex justify-end gap-2 pt-2">
        <p-button label="Cancel" severity="secondary" (onClick)="cancel()" />
        <p-button label="Add Task" (onClick)="submit()" />
      </div>
    </div>
  `,
})
export class FaTaskAddDialogComponent {
  private _ref = inject(DynamicDialogRef);
  private _config = inject(DynamicDialogConfig);

  readonly statusOptions = STATUS_OPTIONS;
  readonly priorityOptions = PRIORITY_OPTIONS;
  readonly recurrenceOptions = RECURRENCE_OPTIONS;
  readonly submitted = signal(false);
  readonly opportunityOptions: { label: string; value: string }[];

  labelsInput = '';

  readonly form = signal<Partial<Omit<FaTask, 'id' | 'createdDate' | 'updatedDate' | 'subtasks' | 'labels'>>>({
    taskName: '',
    description: '',
    assignee: '',
    opportunityId: null,
    priority: 'P3',
    status: 'To Do',
    dueDate: '',
    plannedDate: '',
    recurrence: 'none',
    recurrenceCustomText: '',
  });

  constructor() {
    const opportunities: Opportunity[] = this._config.data?.opportunities ?? [];
    this.opportunityOptions = opportunities.map((o) => ({
      label: o.opportunityDescription,
      value: o.id,
    }));
  }

  patchForm(key: string, value: unknown): void {
    this.form.update((f) => ({ ...f, [key]: value }));
  }

  cancel(): void {
    this._ref.close(null);
  }

  submit(): void {
    this.submitted.set(true);
    const f = this.form();
    if (!f.taskName || !f.assignee || !f.priority || !f.status || !f.dueDate) return;

    const labels = this.labelsInput
      .split(',')
      .map((l) => l.trim())
      .filter(Boolean);

    const now = new Date().toISOString().slice(0, 10);
    const result: Omit<FaTask, 'id'> = {
      taskName: f.taskName,
      description: f.description ?? '',
      assignee: f.assignee,
      opportunityId: f.opportunityId ?? null,
      priority: f.priority,
      status: f.status,
      dueDate: f.dueDate,
      plannedDate: f.plannedDate ?? '',
      labels,
      subtasks: [],
      recurrence: f.recurrence ?? 'none',
      recurrenceCustomText: f.recurrenceCustomText ?? '',
      createdDate: now,
      updatedDate: now,
    };
    this._ref.close(result);
  }
}
